const COST_KIND_ALIASES = {
  hp: 'health',
};

// The client's copy of the server's ActionClassifier
// (`apps/game/services/action/classifier.py`). It exists so a safe place can
// grey out the blows it will refuse instead of letting the player spend a
// click discovering it — the server stays the authority, and every disagreement
// still surfaces as its own refusal.
const OFFENSIVE_SKILL_TYPES = new Set(['attack', 'debuff']);
const DEFENSIVE_SKILL_TYPES = new Set(['defense', 'heal', 'buff']);
const AGGRESSIVE_IMPACT_KINDS = new Set([
  'damage', 'debuff', 'stun', 'sleep', 'confusion', 'paralysis', 'fear',
  'freeze', 'burn', 'poison', 'slow', 'blind', 'silence', 'bleed', 'disarm',
  'root', 'energydecrease', 'lifesteal',
]);
// Special actions the server classifies as offensive.
const OFFENSIVE_ACTION_TYPES = new Set(['snatchitem', 'startfight']);

function normalizeToken(value) {
  return String(value ?? '').toLowerCase().replace(/[\s_-]+/g, '');
}

function normalizeCostKind(kind) {
  const token = normalizeToken(kind);
  return COST_KIND_ALIASES[token] ?? token;
}

function isAllowedInPhase(allowedPhases, currentPhase) {
  if (!currentPhase || !Array.isArray(allowedPhases) || allowedPhases.length === 0) {
    return true;
  }
  const current = normalizeToken(currentPhase);
  return allowedPhases.some((phase) => normalizeToken(phase) === current);
}

function hasEnoughResources(cost, playerService) {
  if (!Array.isArray(cost) || cost.length === 0) return true;
  if (!playerService) return true;

  return cost.every((entry) => {
    const wanted = normalizeCostKind(entry?.kind);
    const attribute = playerService
      .getAttributes?.()
      ?.find((attr) => normalizeCostKind(attr?.name) === wanted);
    const currentValue = attribute
      ? attribute.current
      : playerService.getCurrentAttributeValue?.(entry?.kind);
    return currentValue !== null && currentValue !== undefined && currentValue >= entry?.value;
  });
}

// An ability reaches the HUD in three shapes — a bare skill, a world item
// wrapping one, or a special action carrying its own list — and every consumer
// that wants the price had to know which it was holding.
function costOf(ability) {
  if (!ability) return [];
  const cost = ability.cost ?? ability.item?.skill?.cost ?? ability.skill?.cost;
  return Array.isArray(cost) ? cost : [];
}

// Summed rather than found: nothing forbids a skill listing one kind twice, and
// showing only the first entry would under-quote the price.
function costAmount(cost, kind) {
  if (!Array.isArray(cost)) return 0;
  const wanted = normalizeCostKind(kind);
  return cost.reduce(
    (total, entry) =>
      normalizeCostKind(entry?.kind) === wanted ? total + (Number(entry?.value) || 0) : total,
    0,
  );
}

function impactsOf(skill) {
  const impacts = skill?.impact;
  if (Array.isArray(impacts)) return impacts;
  return impacts ? [impacts] : [];
}

function isOffensiveSkill(skill) {
  if (!skill) return false;
  const type = normalizeToken(skill.type);
  if (OFFENSIVE_SKILL_TYPES.has(type)) return true;
  if (DEFENSIVE_SKILL_TYPES.has(type)) return false;
  return impactsOf(skill).some((impact) =>
    AGGRESSIVE_IMPACT_KINDS.has(normalizeToken(impact?.kind)));
}

function isOffensiveItem(worldItem) {
  return isOffensiveSkill(worldItem?.item?.skill);
}

function isOffensiveSpecial(special) {
  if (!special) return false;
  if (OFFENSIVE_ACTION_TYPES.has(normalizeToken(special.action_type))) return true;
  return isOffensiveSkill(special.skill);
}

function canUseSkill(skill, playerService, currentPhase) {
  if (!playerService) return true;
  if (!isAllowedInPhase(skill?.allowed_phases, currentPhase)) return false;
  return hasEnoughResources(skill?.cost, playerService);
}

function canUseItem(worldItem, playerService, currentPhase) {
  if (!playerService || !worldItem?.item || !worldItem.item.skill) return true;
  if (!isAllowedInPhase(worldItem.item.allowed_phases, currentPhase)) return false;
  return hasEnoughResources(worldItem.item.skill.cost, playerService);
}

function canUseSpecial(special, playerService, currentPhase) {
  if (!playerService) return true;
  if (!isAllowedInPhase(special?.allowed_phases, currentPhase)) return false;
  return hasEnoughResources(special?.cost, playerService);
}

// The one question every cast path asks: may this ability be used right now?
// It exists because the phase used to be checked with a blanket "ACTION only"
// in the tactical cast handler, which refused Breakthrough in the only phase it
// can ever be taken in.
function canUseAbility(ability, playerService, currentPhase) {
  if (!ability) return false;
  if (ability.action_type) return canUseSpecial(ability, playerService, currentPhase);
  if (ability.item) return canUseItem(ability, playerService, currentPhase);
  return canUseSkill(ability, playerService, currentPhase);
}

export default {
  normalizeToken,
  isOffensiveSkill,
  isOffensiveItem,
  isOffensiveSpecial,
  normalizeCostKind,
  costOf,
  costAmount,
  isAllowedInPhase,
  hasEnoughResources,
  canUseSkill,
  canUseItem,
  canUseSpecial,
  canUseAbility,
};
